import type {
  NodeSpec,
  Node as ProsemirrorNode,
  DOMOutputSpec,
  AttributeSpec,
} from 'prosemirror-model';

import type { RtSchemaType } from '../../editor/types';
import { AlignExtension } from '../../extensions/align';
import { HtmlClassExtension } from '../../extensions/html-class';
import { NodeName } from '../../schema/nodes';

export interface HeadingAttrs {
  level: number;
}

export class HeadingNode implements NodeSpec {
  public static readonly nodeName = NodeName.Heading;
  public static is(node: ProsemirrorNode) {
    return node.type.name === HeadingNode.nodeName;
  }
  private readonly alignExtension?: AlignExtension;
  private readonly htmlClassExtension?: HtmlClassExtension;

  public attrs: {
    [name: string]: AttributeSpec;
  } = {
    level: { default: 1 },
    ...(this.config?.withAlign && AlignExtension.attrs),
    ...(this.config?.withHtmlClass && HtmlClassExtension.attrs),
  };
  public content = 'inline*';
  public group = 'block';

  public parseDOM = [1, 2, 3, 4, 5, 6].map(level => ({
    tag: `h${level}`,
    getAttrs: (node: HTMLElement) => ({
      level: level <= 4 ? level : 4,
      ...(this.alignExtension && this.alignExtension.parseDOMGetAttrs(node)),
      ...(this.htmlClassExtension && this.htmlClassExtension.parseDOMGetAttrs(node)),
    }),
  }));

  constructor(
    private config?: {
      withAlign?: boolean;
      withHtmlClass?: boolean;
    },
  ) {
    if (config?.withAlign) {
      this.alignExtension = new AlignExtension();
    }
    if (config?.withHtmlClass) {
      this.htmlClassExtension = new HtmlClassExtension();
    }
  }

  public toDOM = (node: ProsemirrorNode): DOMOutputSpec => {
    let attributes = {};
    if (this.alignExtension) {
      attributes = {
        style: this.alignExtension.getStyleSerialization(node),
      };
    }
    if (this.htmlClassExtension) {
      const htmlClass = this.htmlClassExtension.getClassSerialization(node);
      if (htmlClass) {
        attributes = {
          ...attributes,
          class: htmlClass,
        };
      }
    }
    return [`h${node.attrs.level}`, attributes, 0];
  };
}

export type HeadingSchema = RtSchemaType<typeof HeadingNode>;
